import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';

export interface BatchStudentOption {
  id: string;
  first_name: string;
  last_name: string;
  active: boolean;
}

interface BatchStudentPickerProps {
  /** Currently selected student id, or '' when the run is unscoped. */
  value: string;
  onChange: (studentId: string, student: BatchStudentOption | null) => void;
  /** Only rendered while the owning modal is open, so fetching is cheap. */
  enabled: boolean;
  label?: string;
  helpText?: string;
  inputId?: string;
}

const MAX_SUGGESTIONS = 20;

function formatStudentName(student: BatchStudentOption): string {
  return `${student.last_name}, ${student.first_name}`;
}

/**
 * Type-ahead student selector shared by the monthly-invoice and autopay batch modals.
 * Selecting a student scopes the run to that student and disables the count limit,
 * mirroring `bhcs batch run-invoices --student` / `run-autopay --student`.
 */
export default function BatchStudentPicker({
  value,
  onChange,
  enabled,
  label = 'Student (optional)',
  helpText,
  inputId = 'batch-student',
}: BatchStudentPickerProps) {
  const [students, setStudents] = useState<BatchStudentOption[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from('students')
          .select('id, first_name, last_name, active')
          .eq('active', true)
          .order('last_name', { ascending: true })
          .order('first_name', { ascending: true });
        if (cancelled) return;
        if (error) {
          setLoadError(error.message);
          return;
        }
        setLoadError(null);
        setStudents((data ?? []) as BatchStudentOption[]);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Unknown error');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const selected = useMemo(
    () => students.find((s) => s.id === value) ?? null,
    [students, value],
  );

  const suggestions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return students.slice(0, MAX_SUGGESTIONS);
    return students
      .filter((s) => `${s.first_name} ${s.last_name}`.toLowerCase().includes(needle)
        || formatStudentName(s).toLowerCase().includes(needle))
      .slice(0, MAX_SUGGESTIONS);
  }, [students, query]);

  // Close the dropdown when focus moves outside the picker.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const select = (student: BatchStudentOption) => {
    onChange(student.id, student);
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    onChange('', null);
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">{label}</label>
      {selected ? (
        <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-blue-300 bg-blue-50 px-3 py-2">
          <span className="text-sm text-blue-900">{formatStudentName(selected)}</span>
          <button
            type="button"
            className="text-xs font-medium text-blue-700 hover:text-blue-900"
            onClick={clear}
          >
            Clear
          </button>
        </div>
      ) : (
        <input
          id={inputId}
          type="text"
          autoComplete="off"
          placeholder="All students — type a name to scope"
          className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
      )}
      {helpText ? <p className="mt-1 text-xs text-gray-500">{helpText}</p> : null}
      {loadError ? <p className="mt-1 text-xs text-red-600">Failed to load students: {loadError}</p> : null}
      {open && !selected && suggestions.length > 0 ? (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {suggestions.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left text-sm text-gray-800 hover:bg-blue-50"
                onClick={() => select(s)}
              >
                {formatStudentName(s)}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {open && !selected && query.trim() && suggestions.length === 0 ? (
        <p className="mt-1 text-xs text-gray-500">No active student matches “{query.trim()}”.</p>
      ) : null}
    </div>
  );
}
